import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageTitle from "@/components/PageTitle";
import Spinner from "../../components/spinner/Spinner";
import {
  DropdownContent,
  DropdownItem,
  DropdownItemIcon,
  DropdownRoot,
} from "../../components/dropdown/Dropdown";
import FiltersFirma from "../../components/filter/FiltersFirma";
import Pagination from "../../components/pagination/IndexPagination";
import { defaultFirmaColumns } from "../../config/firmaConfig";
import { ModalProvider, ModalTrigger } from "../../components/Modal/Modal";
import Table from "../../components/Table/Table";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import { PagedFirmaQueryContext } from "../../context/PagedFirmaQueryProvider";
import EmailIcon from "../../components/icons/EmailIcon";
import DownloadListIcon from "../../components/icons/DownloadListIcon";
import { QUERY_PAGE_SIZE } from "../../api/util";
import HandlingerDropdownTrigger from "../../components/Table/HandlingerDropdownTrigger";
import SendEmailModal from "../../components/sendEmail/SendEmailModal";
import { defaultSetSelected } from "../../context/PagedLinjeQueryProvider";
import { IRowData, SortBy } from "../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { firmaIndexSortOrderKey } from "../../util/localStorageKeys";
import { PagedFirmaQueryField } from "../../interfaces/firma";
import { Notification, useToaster } from "rsuite";
import DownloadPendingIcon from "../../components/icons/DownloadPendingIcon";
import { downloadIndexTableToExcel } from "../../util/tableExportUtils";
import { useSelectAllErrorToast } from "../../hooks/useSelectAllErrorToast";
import WarningIcon from "../../components/icons/WarningIcon";
import { FirmaFilterKeys, QueryFilter } from "../../interfaces/filters";
import Button from "@/components/bricks/button/Button";
import {
  FirmaFiltersContext,
  FirmaFiltersEventContext,
} from "../../context/FiltersContext";
import CloseSmallIcon from "../../components/icons/CloseSmallIcon";

const defaultSortBy: Array<SortBy<PagedFirmaQueryField>> = [
  {
    field: "navn1",
    direction: "asc",
  },
];

const Firma = () => {
  return (
    <div className="l-main-row">
      <PageTitle title="Firmaregister" />
      <FiltersFirma />
      <Main />
    </div>
  );
};

const Main = () => {
  const {
    columns,
    error,
    loading,
    setActiveColumns,
    setQuery,
    pagedResponse,
    query,
    page,
    selection,
    emailableIds,
    selectAllError,
    clearSelectAllError,
  } = useContext(PagedFirmaQueryContext);

  const {
    addSelectedIds,
    removeSelectedIds,
    selectAll,
    selectedIds,
    clearSelection,
    lastSelectedId,
    setLastSelectedId,
  } = selection;

  const filters = useContext(FirmaFiltersContext);
  const updateFilters = useContext(FirmaFiltersEventContext);
  const [clear, setClear] = useState(false);
  const [activeModal, setActiveModal] = useState<ActiveModalTypes>("sendemail");
  const [sortBy, setSortBy] = useLocalStorageState<Array<SortBy>>(
    firmaIndexSortOrderKey,
    { defaultValue: defaultSortBy },
  );

  // navigation
  const navigate = useNavigate();

  // for all your toasting purposes
  const toaster = useToaster();
  useSelectAllErrorToast(selectAllError, clearSelectAllError);

  if (error) {
    return (
      <div className="flex-grow-1 d-flex justify-content-center align-items-center text-muted text-center">
        Noget gik galt ...
        <br />
        {`Fejl: ${error.message}`}
      </div>
    );
  }

  if (loading && !pagedResponse) {
    return <Spinner className="flex-grow-1" label="Henter firmaer ..." />;
  }

  if (!pagedResponse) {
    return null;
  }

  const { firmaer } = pagedResponse;
  const { edges, pageInfo, totalCount } = firmaer;
  const { endCursor, startCursor, hasNextPage, hasPreviousPage } = pageInfo;

  const isAllSelected = totalCount === selectedIds.length;

  function downloadToExcel() {
    if (query) {
      toaster.push(
        <Notification>
          <div className="d-flex flex-row gap-3 align-items-center">
            <span className={`c-theme-icon c-theme-icon--firma`}>
              <DownloadPendingIcon />
            </span>
            Forbereder fil download
          </div>
        </Notification>,
        {
          placement: "bottomEnd",
          duration: 2500,
        },
      );

      const promise = downloadIndexTableToExcel(
        selectedIds,
        query,
        "firmaer",
        columns,
        "export-firmaer",
      );

      promise.catch((error) => {
        toaster.push(
          <Notification>
            <div className="d-flex flex-row gap-3 align-items-center">
              <span className={`c-theme-icon bg-danger text-white`}>
                <WarningIcon />
              </span>
              <div className="d-flex flex-column gap-1">
                Der skete en fejl, da vi forberedte filen. Prøv igen eller
                kontakt deres administrator.
                <br />
                <small className="text-muted">
                  {error.status && `Fejl: ${error.status}`}
                </small>
              </div>
            </div>
          </Notification>,
          {
            placement: "bottomEnd",
            duration: 8000,
          },
        );
      });
    }
  }

  return (
    <main className="l-main__content">
      <header className="navbar navbar--sub">
        <div>
          <h2 className="me-2">{totalCount} firmaer</h2>
          {filters
            .filter(
              (f: QueryFilter<FirmaFilterKeys>) => f.filterKey === "search",
            )
            .map((f: QueryFilter<FirmaFilterKeys>) => {
              return (
                <span
                  className="badge badge--fixed-size bg-light text-dark rounded-pill"
                  key={f.filterKey}
                >
                  <span>{f.settings?.query}</span>
                  <Button
                    icon
                    size="sm"
                    color="secondary"
                    onClick={() => {
                      updateFilters({
                        event: "delete",
                        queryFilter: {
                          filterKey: f.filterKey,
                        },
                      });
                    }}
                  >
                    <CloseSmallIcon />
                  </Button>
                </span>
              );
            })}
        </div>
        <div className="justify-content-end gap-5">
          <ModalProvider>
            <DropdownRoot className="c-dropdown-default--actions">
              <HandlingerDropdownTrigger
                allSelected={isAllSelected}
                selectedIdsLength={selectedIds.length}
                totalCount={totalCount}
                type="firmaer"
                badgeSolid
              />
              <DropdownContent>
                <DropdownItem asChild>
                  <ModalTrigger
                    disabled={emailableIds?.length ? false : true}
                    onClick={() => {
                      setClear(true);
                      setActiveModal("sendemail");
                    }}
                  >
                    <DropdownItemIcon>
                      <EmailIcon />
                    </DropdownItemIcon>
                    <span>
                      Send e-mail ...
                      {emailableIds?.length !== 0 && (
                        <div className="badge rounded-pill">
                          {emailableIds?.length}
                        </div>
                      )}
                    </span>
                  </ModalTrigger>
                </DropdownItem>
                <DropdownItem onClick={downloadToExcel}>
                  <DropdownItemIcon>
                    <DownloadListIcon />
                  </DropdownItemIcon>
                  <span>Hent til Excel</span>
                </DropdownItem>
              </DropdownContent>
            </DropdownRoot>
            <Pagination
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              page={page}
              totalCount={totalCount}
              onPreviousClick={() => {
                setQuery({
                  ...query,
                  page: page - 1,
                  after: undefined,
                  before: startCursor,
                });
              }}
              onNextClick={() => {
                setQuery({
                  ...query,
                  page: page + 1,
                  before: undefined,
                  after: endCursor,
                });
              }}
            />
            {activeModal === "sendemail" && (
              <SendEmailModal
                type="firma"
                contextData={{
                  allSelected: isAllSelected,
                  selectedIds: emailableIds,
                  totalCount: emailableIds.length,
                }}
                clear={clear}
                setClear={setClear}
              />
            )}
          </ModalProvider>
        </div>
      </header>
      <div className="l-main__table px-2">
        <ModalProvider>
          <Table
            onRowClick={(data: IRowData) => {
              navigate(`/firma/${data.id}`);
            }}
            className="table--selectable"
            columns={columns}
            keyField="id"
            data={edges.map((e) => {
              return {
                ...e.node,
                selected: selectedIds.indexOf(e.node.id) > -1,
              };
            })}
            selectRow={{
              setSelected: (rowData, selected, shiftKeyPressed) => {
                defaultSetSelected(
                  rowData,
                  selected,
                  shiftKeyPressed,
                  edges,
                  lastSelectedId,
                  addSelectedIds,
                  removeSelectedIds,
                  setLastSelectedId,
                );
              },
              toggleSelectAllOnPage: (mode) => {
                if (mode === "all") {
                  addSelectedIds(edges.map((e) => e.node.id));
                } else {
                  removeSelectedIds(edges.map((e) => e.node.id));
                }
              },
              isAllSelected: isAllSelected,
              selectAll,
              clearSelection,
            }}
            pageInfo={{
              pageSize: QUERY_PAGE_SIZE,
              totalNumberOfRows: totalCount,
            }}
            sortings={{
              defaultSortBy: defaultSortBy,
              setSortBy: setSortBy,
              sortBy: sortBy,
            }}
          />
          <ColumnEditorModal
            columns={columns}
            setActiveColumns={setActiveColumns}
            defaultColumns={defaultFirmaColumns}
          />
        </ModalProvider>
      </div>
    </main>
  );
};

export default Firma;
